import { Button } from '../../components/ui/button';
import { Flame, Loader2, HelpCircle } from 'lucide-react';

export function SlideStage({ current, currentIndex, onStartLiveQuiz, launchingLive, canHost }) {
  if (current?.type === 'quiz') {
    return (
      <div className="text-center space-y-6 max-w-md" data-testid="quiz-intro-card">
        <div className="w-20 h-20 mx-auto rounded-full bg-[var(--lumyn-live)]/10 ring-1 ring-[var(--lumyn-live)]/30 flex items-center justify-center">
          <Flame className="w-10 h-10 text-[var(--lumyn-live)]" />
        </div>
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Live quiz section</p>
          <h2 className="font-display text-3xl font-bold tracking-tight text-white">{current.sectionTitle}</h2>
          <p className="text-sm text-slate-400">
            {canHost
              ? 'Trainees join via the 6-digit code on their phones. The presentation will resume here after the live quiz ends.'
              : 'Wait for your trainer to start the live quiz. Once it ends, the presentation will continue.'}
          </p>
        </div>
        {canHost ? (
          <Button
            size="lg"
            onClick={onStartLiveQuiz}
            disabled={launchingLive}
            className="bg-[var(--lumyn-live)] hover:bg-[var(--lumyn-live)]/90 text-white"
            data-testid="start-quiz-btn"
          >
            {launchingLive ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Flame className="w-4 h-4 mr-2" />}
            Start Live Quiz
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 text-slate-400 text-sm">
            <HelpCircle className="w-4 h-4" />Waiting for trainer…
          </div>
        )}
      </div>
    );
  }

  // Video slide — native <video> for uploads, sandboxed <iframe> for embeds.
  // Per the BPO/trainer-led pivot, videos render on the trainer's screen
  // only. Participants never see this surface. No autoplay (browser would
  // block sound anyway). Trainer presses play; native controls stay visible.
  if (current?.slide?.media_type === 'video') {
    const s = current.slide;
    const isEmbed = s.media_source === 'youtube' || s.media_source === 'vimeo';
    return (
      <figure className="w-full max-w-5xl mx-auto space-y-3" data-testid="slide-video">
        <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg bg-black">
          {isEmbed ? (
            <iframe
              src={s.media_url}
              title={s.title || s.media_caption || 'Video'}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              data-testid="slide-video-embed"
            />
          ) : (
            <video
              src={s.media_url}
              poster={s.media_poster_url || undefined}
              controls
              preload="metadata"
              controlsList="nodownload"
              className="w-full h-full"
              data-testid="slide-video-player"
            />
          )}
        </div>
        {(s.title || s.media_caption) && (
          <figcaption className="text-center text-sm text-slate-300">
            {s.title && <span className="font-medium text-white">{s.title}</span>}
            {s.title && s.media_caption && <span className="mx-2 text-slate-500">·</span>}
            {s.media_caption && <span>{s.media_caption}</span>}
          </figcaption>
        )}
      </figure>
    );
  }

  if (current?.slide?.image_url) {
    return (
      <img
        src={current.slide.image_url}
        alt={current.slide.title || 'Slide'}
        className="max-h-[calc(100vh-200px)] max-w-full object-contain rounded-lg shadow-lg transition-opacity duration-200"
        data-testid="slide-image"
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 p-8 bg-card rounded-xl border" data-testid="slide-content">
      <h2 className="text-xl font-semibold">{current?.slide?.title || `Slide ${currentIndex + 1}`}</h2>
      {current?.slide?.content?.text && (
        <p className="text-muted-foreground leading-relaxed">{current.slide.content.text}</p>
      )}
      {current?.slide?.content?.bullet_points?.length > 0 && (
        <ul className="space-y-2 ml-4">
          {current.slide.content.bullet_points.map((bp, bpi) => (
            <li key={`bp-${bpi}`} className="flex items-start gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--lumyn-content)] mt-1.5 shrink-0" />{bp}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
